import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import AdminBackButton from "@/components/AdminBackButton";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  LogOut, ArrowLeft, Send, Clock, Truck,
  Shield, ClipboardCheck, Check, X, FileCheck, AlertTriangle, Download, Save, Camera
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

export default function ReviewSubmitPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [report, setReport] = useState(null);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, l] = await Promise.all([
          api.get(`/reports/${reportId}`),
          api.get("/lookups"),
        ]);
        setReport(r || null);
        setLookups(l || {});
        // Run validation if not submitted
        if (r && r.status !== "Submitted") {
          try {
            const v = await api.get(`/reports/${reportId}/validate`);
            setValidationErrors(v?.errors || []);
          } catch {}
        }
      } catch (err) {
        console.error("Review page load error:", err);
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportId]);

  const getGps = async () => {
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
      );
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    } catch {
      return {};
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const gps = await getGps();
      await api.put(`/reports/${reportId}/save`, gps);
      toast.success("Report saved — shift end recorded");
      navigate("/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const gps = await getGps();
      await api.put(`/reports/${reportId}/submit`, gps);
      toast.success("Report submitted successfully");
      navigate("/home");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.errors) {
        setValidationErrors(detail.errors);
        toast.error("Validation failed — please fix the errors below");
      } else {
        toast.error(detail || "Failed to submit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!report) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Report not found</div>;

  const siteName = lookups.sites?.find(s => s.id === report.site_id)?.name || "—";
  const isSubmitted = report.status === "Submitted";
  const isSaved = report.status === "Saved";
  const canEdit = !isSubmitted;

  const eqCount = report.equipment_hours?.length || 0;
  const delayCount = report.delay_events?.length || 0;
  const matCount = report.material_movements?.length || 0;
  const hasSafety = report.safety && Object.keys(report.safety).length > 0;
  const qualityCount = report.quality_notes?.length || 0;
  const safetyPhotos = (report.photos || []).filter(p => p.section === "safety");
  const qualityPhotos = (report.photos || []).filter(p => p.section === "quality");
  const totalPhotos = safetyPhotos.length + qualityPhotos.length;

  const warnings = [];
  if (eqCount === 0) warnings.push("No equipment hours logged");
  if (!report.foreman) warnings.push("Operator name missing");

  // Calculate totals (ensure numeric values)
  const totalOpHours = (report.equipment_hours || []).reduce((sum, e) => sum + (Number(e.operating_hours) || 0), 0);
  const totalDelayHours = (report.delay_events || []).reduce((sum, d) => sum + (Number(d.delay_hours) || 0), 0);
  const totalQty = (report.material_movements || []).reduce((sum, m) => sum + (Number(m.quantity) || 0), 0);

  // Per-equipment utilization
  const shiftDuration = 12;
  const equipMap = {};
  const equipLookup = lookups.equipment || [];
  for (const e of (report.equipment_hours || [])) {
    if (!e.equipment_id) continue;
    if (!equipMap[e.equipment_id]) equipMap[e.equipment_id] = { op: 0, delay: 0 };
    equipMap[e.equipment_id].op += (Number(e.operating_hours) || 0);
  }
  for (const d of (report.delay_events || [])) {
    if (!d.equipment_id) continue;
    if (!equipMap[d.equipment_id]) equipMap[d.equipment_id] = { op: 0, delay: 0 };
    equipMap[d.equipment_id].delay += (Number(d.delay_hours) || 0);
  }
  const equipUtilization = Object.entries(equipMap).map(([id, hrs]) => {
    const eq = equipLookup.find(e => e.id === id);
    const name = eq ? `${eq.unit_id}` : id.slice(0, 8);
    const type = eq?.equipment_type || "";
    const total = hrs.op + hrs.delay;
    const pct = Math.min(Math.round((total / shiftDuration) * 100), 100);
    const over = total > shiftDuration;
    return { id, name, type, op: hrs.op, delay: hrs.delay, total, pct, over };
  }).sort((a, b) => b.total - a.total);

  const canExportPdf = ["admin", "viewer"].includes(user?.role);

  const handleDownloadPdf = async (theme) => {
    try {
      const token = localStorage.getItem("token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/reports/${reportId}/pdf?theme=${theme}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${report.report_date}_${report.shift}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div className="min-h-screen" data-testid="review-submit-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <AdminBackButton />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(user?.role === "viewer" ? "/reports" : `/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> {user?.role === "viewer" ? "Reports" : "Dashboard"}
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileCheck className="h-6 w-6 text-primary" strokeWidth={1.5} />
            {isSubmitted ? "Report Summary" : "Review & Submit"}
          </h1>
          <div className="flex items-center gap-2">
            {isSubmitted && canExportPdf && (
              <>
                <Button variant="outline" size="sm" className="border-border text-xs" onClick={() => handleDownloadPdf("light")} data-testid="pdf-light-button">
                  <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> PDF
                </Button>
                <Button variant="outline" size="sm" className="border-primary/30 text-primary text-xs" onClick={() => handleDownloadPdf("dark")} data-testid="pdf-dark-button">
                  <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Dark PDF
                </Button>
              </>
            )}
            <Badge variant="outline" className={
              isSubmitted
                ? "bg-primary/20 text-primary border-primary/30"
                : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
            }>
              {report.status}
            </Badge>
          </div>
        </div>

        {/* Header Summary */}
        <Card className="border-border bg-card mb-4">
          <CardContent className="py-4 px-5">
            <div className="grid grid-cols-2 gap-y-3 gap-x-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Date</p>
                <p className="font-mono text-sm mt-0.5">{report.report_date}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Site</p>
                <p className="text-sm mt-0.5">{siteName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift</p>
                <p className="text-sm mt-0.5">{report.shift}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Operator / Foreman</p>
                {report.status !== "Submitted" ? (
                  <Input
                    value={report.foreman || ""}
                    onChange={(e) => setReport({ ...report, foreman: e.target.value })}
                    onBlur={async () => {
                      if (report.foreman) {
                        try {
                          await api.put(`/reports/${reportId}`, { foreman: report.foreman });
                        } catch {}
                      }
                    }}
                    className="mt-1 h-8 text-sm"
                    placeholder="Enter operator/foreman name"
                    data-testid="foreman-input"
                  />
                ) : (
                  <p className="text-sm mt-0.5">{report.foreman || "—"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift Start</p>
                <p className="font-mono text-sm mt-0.5">
                  {report.shift_start ? new Date(report.shift_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift End</p>
                <p className="font-mono text-sm mt-0.5">
                  {report.shift_end
                    ? new Date(report.shift_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : <span className="text-yellow-400">Recorded on submit</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Module Summaries */}
        <div className="space-y-3 mb-6">
          <Card className="border-border bg-card">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-blue-400" strokeWidth={1.5} />
                <span className="text-sm">Equipment Hours</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="kpi-value text-sm">{eqCount} entries</span>
                <span className="text-xs text-muted-foreground">
                  Total: {totalOpHours.toFixed(1)}h
                </span>
                {eqCount > 0 ? <Check className="h-4 w-4 text-primary" strokeWidth={2} /> : <X className="h-4 w-4 text-muted-foreground" strokeWidth={2} />}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-4 w-4 text-orange-400" strokeWidth={1.5} />
                <span className="text-sm">Delays</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="kpi-value text-sm">{delayCount} entries</span>
                <span className="text-xs text-muted-foreground">
                  Total: {totalDelayHours.toFixed(1)}h
                </span>
                {delayCount > 0 ? <Check className="h-4 w-4 text-primary" strokeWidth={2} /> : <span className="text-xs text-muted-foreground">None</span>}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-amber-400" strokeWidth={1.5} />
                <span className="text-sm">Material Movements</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="kpi-value text-sm">{matCount} entries</span>
                <span className="text-xs text-muted-foreground">Total: {totalQty.toFixed(1)}</span>
                {matCount > 0 ? <Check className="h-4 w-4 text-primary" strokeWidth={2} /> : <X className="h-4 w-4 text-muted-foreground" strokeWidth={2} />}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-red-400" strokeWidth={1.5} />
                <span className="text-sm">Safety & Environment</span>
              </div>
              <div className="flex items-center gap-2">
                {safetyPhotos.length > 0 && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Camera className="h-3 w-3" strokeWidth={1.5} />{safetyPhotos.length}
                  </span>
                )}
                {hasSafety ? <Check className="h-4 w-4 text-primary" strokeWidth={2} /> : <X className="h-4 w-4 text-muted-foreground" strokeWidth={2} />}
                <span className="text-xs text-muted-foreground">{hasSafety ? "Recorded" : "Not recorded"}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ClipboardCheck className="h-4 w-4 text-purple-400" strokeWidth={1.5} />
                <span className="text-sm">Quality Notes</span>
              </div>
              <div className="flex items-center gap-2">
                {qualityPhotos.length > 0 && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Camera className="h-3 w-3" strokeWidth={1.5} />{qualityPhotos.length}
                  </span>
                )}
                <span className="kpi-value text-sm">{qualityCount}</span>
                {qualityCount > 0 ? <Check className="h-4 w-4 text-primary" strokeWidth={2} /> : <span className="text-xs text-muted-foreground">None</span>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-Equipment Utilization */}
        {equipUtilization.length > 0 && (
          <div className="mb-6" data-testid="equipment-utilization-summary">
            <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Equipment Utilization</h2>
            <Card className="border-border bg-card overflow-hidden">
              <CardContent className="py-4 px-5 space-y-4">
                {equipUtilization.map((eq) => (
                  <div key={eq.id} data-testid={`equip-util-${eq.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{eq.name}</span>
                        {eq.type && <span className="text-xs text-muted-foreground">{eq.type}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs ${eq.over ? "text-red-400" : "text-muted-foreground"}`}>
                          {eq.total.toFixed(1)}h / {shiftDuration}h
                        </span>
                        <span className={`font-mono text-xs font-semibold ${eq.over ? "text-red-400" : eq.pct >= 80 ? "text-primary" : "text-muted-foreground"}`}>
                          {eq.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${eq.over ? "bg-red-500" : eq.pct >= 80 ? "bg-primary" : eq.pct >= 50 ? "bg-blue-500" : "bg-muted-foreground/40"}`}
                        style={{ width: `${Math.min(eq.pct, 100)}%` }}
                      />
                    </div>
                    <div className="flex gap-3 mt-1">
                      <span className="text-[10px] text-muted-foreground">Operating: {eq.op.toFixed(1)}h</span>
                      {eq.delay > 0 && <span className="text-[10px] text-orange-400/70">Delay: {eq.delay.toFixed(1)}h</span>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && !isSubmitted && (
          <Card className="border-red-500/30 bg-red-500/10 mb-6" data-testid="validation-errors">
            <CardContent className="py-3 px-5">
              <p className="text-red-400 text-sm font-medium mb-1">Validation Errors</p>
              {validationErrors.map((e, i) => (
                <p key={i} className="text-red-300/80 text-xs">- {e}</p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Warnings */}
        {warnings.length > 0 && !isSubmitted && validationErrors.length === 0 && (
          <Card className="border-yellow-500/30 bg-yellow-500/10 mb-6">
            <CardContent className="py-3 px-5">
              <p className="text-yellow-400 text-sm font-medium mb-1">Warnings</p>
              {warnings.map((w, i) => (
                <p key={i} className="text-yellow-300/80 text-xs">- {w}</p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Saved info */}
        {isSaved && report.shift_end && (
          <Card className="border-blue-500/30 bg-blue-500/10 mb-6">
            <CardContent className="py-3 px-5">
              <p className="text-blue-400 text-sm font-medium">
                Saved offline — Shift end recorded: {new Date(report.shift_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-blue-300/80 text-xs mt-1">Submit when back online. Submit timestamp will not affect shift duration.</p>
            </CardContent>
          </Card>
        )}

        {/* Submitted info */}
        {isSubmitted && report.submitted_on && (
          <Card className="border-primary/30 bg-primary/10 mb-6">
            <CardContent className="py-3 px-5">
              <p className="text-primary text-sm font-medium">
                Submitted on {new Date(report.submitted_on).toLocaleString()}
              </p>
              {report.shift_end && (
                <p className="text-primary/80 text-xs mt-1">
                  Shift end recorded: {new Date(report.shift_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {canEdit && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground text-center">
              {isSaved ? "Submit the report when you are back online" : "Save offline or submit to finalize"}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-border"
                onClick={() => navigate(`/report/${reportId}/dashboard`)}
                data-testid="back-to-edit-button"
              >
                Back to Edit
              </Button>
              {!isSaved && (
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-report-button"
                  className="flex-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                >
                  <Save className="h-4 w-4 mr-2" strokeWidth={2} />
                  {saving ? "Saving..." : "Save Report"}
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                disabled={submitting || validationErrors.length > 0}
                data-testid="submit-report-button"
                className="flex-1 font-headline font-semibold"
              >
                <Send className="h-4 w-4 mr-2" strokeWidth={2} />
                {submitting ? "Submitting..." : "Submit Report"}
              </Button>
            </div>
          </div>
        )}

        {isSubmitted && (
          <Button
            variant="outline"
            className="w-full border-border"
            onClick={() => navigate("/home")}
            data-testid="back-to-home-button"
          >
            Back to Home
          </Button>
        )}
      </main>
    </div>
  );
}
